import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import HealthSnapshotImg from '../../../assets/health-snapshot.png';
import AvatarGroup, {
  AvatarGroupSize,
  AvatarGroupStyle,
} from '../../../components/Avatar/AvatarGroup';
import Button from '../../../components/Button/Button';
import CircledIcon from '../../../components/CircledIcon';
import GenericCard from '../../../components/GenericCards/GenericCards';
import useMobile from '../../../hooks/useMobileHook';
import { HEALTH_INSURANCE_ROUTE, HEALTH_SUMMARY } from '../../../routes/types';
import PopoverWithLinks from '../PopoverLinks/PopoverLinks';

const healthSnapshotOptions = [
  {
    linkHeader: 'Call 555-555-5555',
    route: '#',
    icon: light('phone'),
    disableRightIcon: true,
    types: ['medical', 'dental', 'vision'],
  },

  {
    linkHeader: 'Website',
    route: `http://aetna.com/`,
    icon: light('globe'),
    rightIcon: light('external-link'),
    types: ['medical', 'dental', 'vision'],
    target: '_blank',
  },

  {
    linkHeader: 'Plan Info',
    route: `${process.env.PUBLIC_URL}/${'blank-demo-example.pdf'}`,
    icon: light('file-pdf'),
    rightIcon: light('external-link'),
    types: ['medical', 'dental', 'vision'],
    target: '_blank',
  },

  {
    linkHeader: 'ID Card',
    route: `id-card`,
    icon: light('id-card'),
    rightIcon: light('chevron-right'),
    types: ['medical'],
    modal: true,
  },
];

const emptyPlans = [
  {
    name: 'Medical',
    subTitle: 'Waived',
    icon: 'heart-pulse',
  },

  {
    name: 'Dental',
    subTitle: 'Waived',
    icon: 'tooth',
  },

  {
    name: 'Vision',
    subTitle: 'Waived',
    icon: 'glasses',
  },
];

const HealthSnapshot = ({
  transperant = false,
  coveredFamilyMembers,
  isWaived = false,
  linksData,
}: any) => {
  const isMobile = useMobile();
  const navigate = useNavigate();

  const icon = (iconName: IconProp) => (
    <CircledIcon
      variant="icon-emphasized"
      size={isMobile ? 'icon-sm' : 'icon-lg'}
      icon={iconName}
    />
  );

  const getCardIcon = (type: String) => {
    switch (type) {
      case 'heart-pulse':
        return light('heart-pulse');
      case 'tooth':
        return light('tooth');
      case 'glasses':
        return light('glasses');
      default:
        return light('glasses');
    }
  };

  return (
    <Row className="m-0 p-0 d-flex justify-content-between">
      <Row className={'m-0 p-0'}>
        <Col
          className={
            'p-0 d-flex justify-content-between align-items-center mb-10'
          }
        >
          <h4 className={'text-sm-20-lg-32 m-0 lh-xs'}>{'Health Snapshot'}</h4>

          <section className={'align-items-center'}>
            <PopoverWithLinks linksData={linksData} />
          </section>
        </Col>
      </Row>

      <Container className={`p-0 ${transperant && 'border rounded-1'}`}>
        <GenericCard variant={`${transperant ? 'transperant' : 'white'}`}>
          <Row className="m-0 px-6 px-lg-0">
            {!isWaived ? (
              <>
                {coveredFamilyMembers ? (
                  <>
                    <Row className="px-0 mx-0 mb-3 pb-3 border-bottom">
                      <Col className="col-6 px-0 text-start text-sm-12-lg-14 fw-light text-ligther-grey">
                        Plan
                      </Col>
                      <Col className="col-6 px-0 mb-0 text-start text-sm-12-lg-14 fw-light text-ligther-grey">
                        Covered Family Members
                      </Col>
                    </Row>

                    {coveredFamilyMembers.map(
                      (snapshot: any, index: number) => (
                        <Row
                          key={index}
                          className={`px-0 mx-0 pb-3 ${
                            coveredFamilyMembers.length - 1 === index
                              ? ''
                              : 'mb-3 border-bottom'
                          }`}
                        >
                          <Col className="col-12 col-lg-6 px-0 text-start text-sm-12-lg-14 fw-light text-ligther-grey">
                            <Row className="m-0">
                              <Col className="p-0 col-12 col-lg-12 d-flex">
                                {icon(getCardIcon(snapshot['generic']['icon']))}
                                <div className="d-flex flex-column justify-content-center ps-4">
                                  <div className="fw-normal text-sub1">
                                    <span>{snapshot['generic']['title']}</span>
                                  </div>
                                  <div className="mt-1 fw-lighter text-caption">
                                    <span>{snapshot['plan']['title']}</span>
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </Col>
                          <Col className="col-12 col-lg-6 mt-6 mt-lg-0 px-0 text-start text-sm-12-lg-14 fw-light text-ligther-grey">
                            <Row className="m-0 d-flex flex-row justify-content-between">
                              <Col className="p-0 d-flex align-items-center col-2 col-lg-6">
                                <div>
                                  <AvatarGroup
                                    avatars={snapshot['profiles']}
                                    groupSize={AvatarGroupSize.MINIMIZED}
                                    groupStyle={AvatarGroupStyle.STACKED}
                                    hasTooltip
                                  />
                                </div>
                              </Col>
                              <Col className="p-0 d-flex justify-content-end ps-lg-10 col-lg-6">
                                <PopoverWithLinks
                                  linksData={healthSnapshotOptions.filter(
                                    (option) =>
                                      option.types.includes(snapshot['type'])
                                  )}
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      )
                    )}
                  </>
                ) : (
                  <>
                    <Row className="m-0 p-0 py-11">
                      <Col className="p-0 d-flex flex-column justify-content-lg-center">
                        <div className="p-0 d-flex justify-content-center justify-content-lg-start">
                          <h5 className="m-0 fw-normal">
                            Welcome to Benefits!
                          </h5>
                        </div>
                        <div className="p-0 mt-1 d-flex justify-content-center justify-content-lg-start fw-light text-sub1 text-darker">
                          <span className={'lh-xs'}>
                            If you're a new hire or had a change in employment
                            status, we're happy to introduce you to the benefits
                            that are available.
                          </span>
                        </div>
                        <div className="p-0 mt-10 d-flex justify-content-center justify-content-lg-start">
                          <Button
                            onClick={() => navigate(HEALTH_SUMMARY)}
                            variant="primary-outline"
                            className={'order-1 order-lg-3 mt-3 mt-lg-0 me-7'}
                            size="sm"
                          >
                            Learn more
                          </Button>
                          <Button
                            onClick={() => navigate(HEALTH_INSURANCE_ROUTE)}
                            variant="primary"
                            className={'order-1 order-lg-3 mt-3 mt-lg-0'}
                            size="sm"
                          >
                            Enroll Now
                          </Button>
                        </div>
                      </Col>
                      <Col className="p-0 mt-7 mt-lg-0 d-flex flex-column align-items-center">
                        <img
                          src={HealthSnapshotImg}
                          alt="health-snapshot-img"
                        />
                      </Col>
                    </Row>

                    <Row className="px-0 mx-0 mb-3 pb-3 border-bottom">
                      <Col className="col-6 px-0 text-start text-sm-12-lg-14 fw-normal text-ligther-grey">
                        Plan
                      </Col>
                    </Row>
                    {emptyPlans.map((e, index) => (
                      <Row
                        key={index}
                        className={`px-0 mx-0 pb-3 ${
                          emptyPlans.length - 1 === index
                            ? ''
                            : 'mb-3 border-bottom'
                        }`}
                      >
                        <Col className="col-12 col-lg-6 px-0 text-start text-sm-12-lg-14 fw-light text-ligther-grey">
                          <Row className="m-0">
                            <Col className="p-0 col-12 col-lg-12 d-flex">
                              {icon(getCardIcon(e.icon))}
                              <div className="d-flex flex-column justify-content-center ps-4">
                                <div className="fw-normal text-sub1">
                                  <span>{e.name}</span>
                                </div>
                                <div className="mt-1 fw-lighter text-caption">
                                  <span>{e.subTitle}</span>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    ))}
                  </>
                )}
              </>
            ) : (
              <>
                {emptyPlans.map((e, index) => (
                  <Row
                    key={index}
                    className={`px-0 mx-0 pb-3 ${
                      emptyPlans.length - 1 === index
                        ? ''
                        : 'mb-3 border-bottom'
                    }`}
                  >
                    <Col className="col-12 col-lg-6 px-0 text-start text-sm-12-lg-14 fw-light text-ligther-grey">
                      <Row className="m-0">
                        <Col className="p-0 col-12 col-lg-12 d-flex">
                          {icon(getCardIcon(e.icon))}
                          <div className="d-flex flex-column justify-content-center ps-4">
                            <div className="fw-normal text-sub1">
                              <span>{e.name}</span>
                            </div>
                            <div className="mt-1 fw-lighter text-caption">
                              <span>{e.subTitle}</span>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </Row>
        </GenericCard>
      </Container>
    </Row>
  );
};

export default HealthSnapshot;
